import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import config from '../../../config';
import api from '../../api';
import enumCache from '@/shared/services/EnumCacheService';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import intlShape from '@/shared/utils/intlShape';

const messages = defineMessages({
  AllRoles: 'AllRoles',
  DefaultRoles: 'DefaultRoles',
  CustomRoles: 'CustomRoles',
});

class EmployeeRoleSelector extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.shape({
      role: PropTypes.number,
      customRoleId: PropTypes.string,
    }),
    required: PropTypes.bool,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    value: null,
    required: false,
    placeholder: null,
  };

  state = {
    roles: [],
    selectedRole: '',
  };

  async componentDidMount() {
    await this.loadRoles();
  }

  componentDidUpdate(prevProps) {
    const { value, onChange } = this.props;
    const { roles } = this.state;

    if (JSON.stringify(prevProps.value) !== JSON.stringify(value)) {
      if (!value || (value.role === null && value.customRoleId === null)) {
        this.setState({ selectedRole: '' });
        return;
      }

      const foundRole = roles.find(
        r => r.role === value.role && r.customRoleId === value.customRoleId,
      );

      if (foundRole) {
        this.setState({
          selectedRole: JSON.stringify({
            role: foundRole.role,
            customRoleId: foundRole.customRoleId,
          }),
        });
      } else {
        this.setState({ selectedRole: '' });
        onChange(null);
      }
    }
  }

  loadRoles = async () => {
    const { intl, value } = this.props;

    try {
      const dataCache = DataCacheService.getDataCache();

      const [tenantRolesResponse, defaultRoles] = await Promise.all([
        api.base.get(`${config.appUrls.api}/api/TenantRole`),
        Promise.resolve(enumCache.getTranslated('role', intl) || []),
      ]);

      const processedDefaults = defaultRoles
        .map(x => ({
          role: x.id,
          label: x.label,
          customRoleId: null,
          customRoleName: null,
          group: intl.formatMessage(messages.DefaultRoles),
        }))
        .sort(dynamicSort('role'));
      const processedCustoms = (tenantRolesResponse.data || [])
        .map(x => ({
          role: 99,
          label: x.name,
          customRoleId: x.id,
          customRoleName: x.name,
          group: intl.formatMessage(messages.CustomRoles),
        }))
        .sort(dynamicSort('label'));
      const allRoles = [...processedDefaults, ...processedCustoms];

      let selectedRole = '';
      if (value) {
        const foundRole = allRoles.find(
          r => r.role === value.role && r.customRoleId === value.customRoleId,
        );
        if (foundRole) {
          selectedRole = JSON.stringify({
            role: foundRole.role,
            customRoleId: foundRole.customRoleId,
          });
        }
      }

      this.setState({ roles: allRoles, selectedRole });
    } catch (error) {
      console.error('Failed to load employee roles:', error);
    }
  };

  selectedValueChanged = e => {
    const { onChange } = this.props;
    const selectedValueString = e.target.value;

    this.setState({ selectedRole: selectedValueString });

    if (selectedValueString) {
      onChange(JSON.parse(selectedValueString));
    } else {
      onChange(null);
    }
  };

  render() {
    const { intl, placeholder, required } = this.props;
    const { roles, selectedRole } = this.state;

    const groupedRoles = roles.reduce((acc, role) => {
      const group = role.group;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(role);
      return acc;
    }, {});

    return (
      <select
        value={selectedRole}
        className="form-control"
        onChange={this.selectedValueChanged}
        required={required}
      >
        {(!required || !selectedRole) && (
          <option value="">{placeholder ?? intl.formatMessage(messages.AllRoles)}</option>
        )}
        {Object.entries(groupedRoles).map(([groupName, groupRoles]) => (
          <optgroup key={groupName} label={groupName}>
            {groupRoles.map(role => (
              <option
                key={`${role.role}-${role.customRoleId}`}
                value={JSON.stringify({ role: role.role, customRoleId: role.customRoleId })}
              >
                {role.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    );
  }
}

export default injectIntl(EmployeeRoleSelector);
